import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { trackInitiateCheckout, trackPurchase } from "../services/facebookAPI";

interface CheckoutPageProps {
  setOrderDetails: (details: any) => void;
}

interface FormData {
  name: string;
  address: string;
  phone: string;
  whatsapp: string;
  state: string;
  deliveryAvailability: string;
  paymentMethod: "paystack" | "delivery";
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ setOrderDetails }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackInitiateCheckout();
    console.log("📊 Facebook: InitiateCheckout event tracked");
  }, []);

  // ---------- State ----------
  const [formData, setFormData] = useState<FormData>({
    name: "",
    address: "",
    phone: "",
    whatsapp: "",
    state: "",
    deliveryAvailability: "",
    paymentMethod: "delivery",
  });

  const [quantity, setQuantity] = useState<number>(1);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------- Pricing Constants ----------
  const BASE_PRICE = 55000;           // price for first unit
  const EXTRA_UNIT_PRICE = 50000;      // each additional unit (after first)
  const ONLINE_DISCOUNT = 5000;         // extra discount for paying online

  // Compute total based on current quantity and payment method
  const getTotalPrice = (qty: number, method: "paystack" | "delivery"): number => {
    const quantityTotal = BASE_PRICE + (qty - 1) * EXTRA_UNIT_PRICE;
    return method === "paystack" ? quantityTotal - ONLINE_DISCOUNT : quantityTotal;
  };

  const totalPrice = getTotalPrice(quantity, formData.paymentMethod);

  // ---------- Nigerian States List ----------
  const nigerianStates = [
    "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue",
    "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT",
    "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi",
    "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo",
    "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara",
  ];

  // ---------- Form Validation ----------
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.address.trim())
      newErrors.address = "Delivery address is required";
    if (!/^[0-9]{11}$/.test(formData.phone))
      newErrors.phone = "Valid 11-digit phone number required";
    if (!/^[0-9]{11}$/.test(formData.whatsapp))
      newErrors.whatsapp = "Valid 11-digit WhatsApp number required";
    if (!formData.state) newErrors.state = "Please select your state";
    if (!formData.deliveryAvailability.trim())
      newErrors.deliveryAvailability = "Please specify delivery availability";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- Form Submission ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      alert("Please correct the errors in the form");
      return;
    }

    setIsSubmitting(true);

    try {
      // Include quantity in order data
      const orderData = {
        ...formData,
        quantity,
        orderDate: new Date().toISOString(),
        product: "Super Gro 5L",
        source: "v1-original",
        amount: totalPrice,
        status: "pending",
      };

      console.log("📦 Saving order...", orderData);

      // Facebook Purchase Event
      const orderId = `SUPERGRO-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 6)
        .toUpperCase()}`;

      const facebookPurchaseTracked = await trackPurchase(orderId, {
        firstName: formData.name.split(" ")[0] || formData.name,
        lastName: formData.name.split(" ").slice(1).join(" ") || "",
        phone: formData.phone,
        email: `${formData.phone}@customer.tpa.com`,
        state: formData.state,
      });

      console.log(
        "📊 Facebook Purchase Event:",
        facebookPurchaseTracked ? "✅ Tracked" : "❌ Failed"
      );

      // 1. Save order to backend
      const orderResponse = await axios.post(
        "https://supergro-backend.onrender.com/api/orders",
        orderData
      );

      console.log("✅ Order saved:", orderResponse.data);

      if (formData.paymentMethod === "paystack") {
        const savedOrderId =
          orderResponse.data.order?._id ||
          orderResponse.data.orderId ||
          orderResponse.data.order?._id;

        if (!savedOrderId) {
          throw new Error("Order ID not received from server");
        }

        console.log("💰 Initializing PayStack for order:", savedOrderId);

        const paymentResponse = await axios.post(
          "https://supergro-backend.onrender.com/api/paystack/initialize",
          {
            email: `${formData.phone}@customer.tpa.com`,
            amount: totalPrice * 100, // convert to kobo
            metadata: {
              customer_name: formData.name,
              phone: formData.phone,
              whatsapp: formData.whatsapp,
              order_id: savedOrderId,
              quantity,
              facebook_tracked: facebookPurchaseTracked,
            },
            callback_url: `${window.location.origin}/thank-you`,
          }
        );

        console.log("🔗 PayStack response:", paymentResponse.data);

        if (
          paymentResponse.data.success &&
          paymentResponse.data.authorization_url
        ) {
          setOrderDetails({
            ...orderData,
            orderId: savedOrderId,
            facebook_tracked: facebookPurchaseTracked,
          });

          setTimeout(() => {
            window.location.href = paymentResponse.data.authorization_url;
          }, 500);
        } else {
          throw new Error(
            paymentResponse.data.error || "PayStack initialization failed"
          );
        }
      } else {
        console.log("✅ Pay on delivery order successful");
        setOrderDetails({
          ...orderData,
          orderId: orderId,
          facebook_tracked: facebookPurchaseTracked,
        });
        navigate("/thank-you");
      }
    } catch (error: any) {
      console.error("❌ Order error:", error);
      let errorMessage = "Failed to place order. Please try again.";
      if (error.response?.data?.error) {
        errorMessage = `Server Error: ${error.response.data.error}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(`Error: ${errorMessage}`);

      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>Complete Your Order</h1>
          <p>Fill in your details for delivery within 48-72 hours</p>
        </div>

        <div className="checkout-grid">
          <div className="checkout-form">
            <form onSubmit={handleSubmit}>
              <div className="card">
                <h2>Delivery Information</h2>

                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <div className="text-danger">{errors.name}</div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      placeholder="0803XXXXXXX"
                    />
                    {errors.phone && (
                      <div className="text-danger">{errors.phone}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">WhatsApp Number *</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.whatsapp}
                      onChange={(e) =>
                        setFormData({ ...formData, whatsapp: e.target.value })
                      }
                      placeholder="0803XXXXXX"
                    />
                    {errors.whatsapp && (
                      <div className="text-danger">{errors.whatsapp}</div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">State *</label>
                  <select
                    className="form-control"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({ ...formData, state: e.target.value })
                    }
                  >
                    <option value="">Select your state</option>
                    {nigerianStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <div className="text-danger">{errors.state}</div>
                  )}
                </div>

                {/* Quantity Selector - placed after state */}
                <div className="form-group">
                  <label className="form-label">How many units do you want? *</label>
                  <select
                    className="form-control"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                  >
                    <option value={1}>Buy 1 for ₦55,000</option>
                    <option value={2}>Buy 2 for ₦105,000 (save ₦5,000)</option>
                    <option value={3}>Buy 3 for ₦155,000 (save ₦10,000)</option>
                    <option value={4}>Buy 4 for ₦200,000 (save ₦15,000)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Full Delivery Address *</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    placeholder="House number, Street, Landmark, City"
                  />
                  {errors.address && (
                    <div className="text-danger">{errors.address}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    When will you be available to receive delivery? *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.deliveryAvailability}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        deliveryAvailability: e.target.value,
                      })
                    }
                    placeholder="e.g., Weekdays 9AM-5PM, Saturday 10AM-3PM"
                  />
                  {errors.deliveryAvailability && (
                    <div className="text-danger">
                      {errors.deliveryAvailability}
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <h2>Payment Method</h2>

                <div className="payment-option">
                  <label className="payment-option-label">
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={formData.paymentMethod === "delivery"}
                      onChange={() =>
                        setFormData({ ...formData, paymentMethod: "delivery" })
                      }
                    />
                    <div className="payment-option-content">
                      <div className="payment-option-header">
                        <span className="payment-option-title">
                          Pay on Delivery
                        </span>
                        <span className="payment-price">
                          ₦{BASE_PRICE.toLocaleString()}
                        </span>
                      </div>
                      <div className="payment-option-desc">
                        <div className="alert alert-warning">
                          <strong>⚠️ IMPORTANT:</strong> You MUST be available
                          at the delivery address. If unavailable, ₦2,000
                          transportation fee applies.
                        </div>
                      </div>
                    </div>
                  </label>
                </div>

                <div className="payment-option">
                  <label className="payment-option-label">
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={formData.paymentMethod === "paystack"}
                      onChange={() =>
                        setFormData({ ...formData, paymentMethod: "paystack" })
                      }
                    />
                    <div className="payment-option-content">
                      <div className="payment-option-header">
                        <span className="payment-option-title">
                          Pay Online with PayStack
                        </span>
                        <span className="payment-discount">
                          Save ₦{ONLINE_DISCOUNT.toLocaleString()}
                        </span>
                      </div>
                      <div className="payment-option-desc">
                        Secure payment with debit card or bank transfer. Instant
                        order confirmation.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="card">
                <label className="terms-agreement">
                  <input type="checkbox" required />
                  <span>
                    I agree to the terms and conditions and understand that
                    delivery will be attempted within 48-72 hours of order
                    confirmation.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-secondary btn-block"
                disabled={isSubmitting}
                style={{ padding: "15px", fontSize: "18px" }}
              >
                {isSubmitting
                  ? "Processing..."
                  : formData.paymentMethod === "paystack"
                  ? `PAY ₦${totalPrice.toLocaleString()} NOW`
                  : "PLACE ORDER (Pay on Delivery)"}
              </button>
            </form>
          </div>

          {/* Order Summary (reflects selected quantity and total) */}
          <div className="order-summary">
            <div className="card">
              <h2>Order Summary</h2>

              <div className="order-item">
                <div className="order-item-name">
                  Super Gro Organic Fertilizer
                </div>
                <div className="order-item-qty">5 Liters × {quantity}</div>
                <div className="order-item-price">
                  ₦{BASE_PRICE.toLocaleString()} first unit
                  {quantity > 1 && (
                    <span style={{ display: 'block', fontSize: '0.9rem', color: '#16a34a' }}>
                      + {quantity - 1} additional × ₦{EXTRA_UNIT_PRICE.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Subtotal (before online discount) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                <span>Subtotal for {quantity} unit{quantity > 1 ? 's' : ''}:</span>
                <span>₦{(BASE_PRICE + (quantity - 1) * EXTRA_UNIT_PRICE).toLocaleString()}</span>
              </div>

              {/* Online Discount (if applicable) */}
              {formData.paymentMethod === "paystack" && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a' }}>
                  <span>Online Payment Discount:</span>
                  <span>-₦{ONLINE_DISCOUNT.toLocaleString()}</span>
                </div>
              )}

              {/* Total */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '2px dashed #e2e8f0'
              }}>
                <span>Total Amount:</span>
                <span>₦{totalPrice.toLocaleString()}</span>
              </div>

              <div className="delivery-info">
                <h3>Delivery Information</h3>
                <p>🚚 <strong>48-72 Hour Delivery</strong></p>
                <p>📍 Nationwide Delivery</p>
                <p>📞 Contact: 0705-526-8702</p>
              </div>

              <div className="security-badges">
                <div className="security-badge"><span>🔒</span> Secure Payment</div>
                <div className="security-badge"><span>✅</span> Money Back Guarantee</div>
                <div className="security-badge"><span>🚚</span> Fast Delivery</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
